import {
    getInstallerUser,
    installerUserLabel
} from './installerTaskCache';

export const makeTransferId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `t${Date.now().toString(36)}${Math.random()
        .toString(36)
        .slice(2, 12)}`;
};

const shortName = value =>
    String(value || '')
        .trim()
        .slice(0, 40);

export const buildTransferPayload = ({
    transferId,
    fromUser,
    lines
}) => {
    const user = fromUser || getInstallerUser() || {};
    const compact = {
        v: 1,
        t: 'TMCTRANSFER',
        id: transferId,
        from: Number(user.id),
        fn: shortName(installerUserLabel(user)),
        ts: Date.now(),
        lines: (lines || []).map(line => ({
            h: Number(line.holding_id || line.id),
            i: Number(line.item_id || line.item?.id),
            q: Number(line.quantity),
            n: shortName(line.name || line.item?.name),
            u: shortName(line.unit || line.item?.unit || 'шт'),
            s: shortName(line.sku || line.item?.sku || '')
        }))
    };
    return JSON.stringify(compact);
};

export const parseTransferQr = raw => {
    const text = String(raw || '').trim();
    if (!text) return null;

    let obj = null;
    const tagged = text.match(/^TMCTRANSFER[:\s]+([\s\S]+)$/i);
    const jsonText = tagged ? tagged[1].trim() : text;

    try {
        obj = JSON.parse(jsonText);
    } catch (_) {
        return null;
    }

    if (!obj || (obj.t !== 'TMCTRANSFER' && obj.type !== 'tmc_transfer')) {
        return null;
    }

    const lines = Array.isArray(obj.lines) ? obj.lines : [];
    if (!obj.id || !obj.from || !lines.length) {
        return null;
    }

    return {
        transferId: String(obj.id),
        fromUserId: Number(obj.from),
        fromName: obj.fn || '',
        createdAt: obj.ts || null,
        lines: lines.map(line => ({
            holding_id: Number(line.h || line.holding_id || 0) || null,
            item_id: Number(line.i || line.item_id),
            quantity: Number(line.q || line.quantity),
            name: line.n || line.name || 'ТМЦ',
            unit: line.u || line.unit || 'шт',
            sku: line.s || line.sku || ''
        }))
    };
};

const cloneHoldings = holdings =>
    (holdings || []).map(row => ({
        ...row,
        item: row.item ? { ...row.item } : row.item
    }));

export const applyOfferToHoldings = (holdings, { transferId, lines }) => {
    const next = cloneHoldings(holdings);

    (lines || []).forEach(line => {
        const qty = Number(line.quantity);
        if (!qty) return;

        let idx = next.findIndex(
            row =>
                Number(row.id) === Number(line.holding_id) &&
                row.status === 'held'
        );
        if (idx < 0) {
            idx = next.findIndex(
                row =>
                    Number(row.item_id || row.item?.id) ===
                        Number(line.item_id) &&
                    row.status === 'held'
            );
        }
        if (idx < 0) return;

        const source = next[idx];
        const left = Number(source.quantity) - qty;
        const pending = {
            ...source,
            id: `local-${transferId}-${line.item_id}`,
            quantity: qty,
            status: 'transfer_pending',
            note: `peer:${transferId}`
        };

        if (left > 0.0001) {
            next[idx] = { ...source, quantity: left };
            next.push(pending);
        } else {
            next[idx] = pending;
        }
    });

    return next;
};

export const applyAcceptToHoldings = (holdings, { transferId, lines }) => {
    const next = cloneHoldings(holdings);

    (lines || []).forEach(line => {
        const qty = Number(line.quantity);
        if (!qty) return;

        const idx = next.findIndex(
            row =>
                Number(row.item_id || row.item?.id) ===
                    Number(line.item_id) &&
                row.status === 'held'
        );

        if (idx >= 0) {
            next[idx] = {
                ...next[idx],
                quantity: Number(next[idx].quantity) + qty
            };
            return;
        }

        next.push({
            id: `local-in-${transferId}-${line.item_id}`,
            item_id: line.item_id,
            quantity: qty,
            status: 'held',
            item: {
                id: line.item_id,
                name: line.name,
                unit: line.unit,
                sku: line.sku
            }
        });
    });

    return next;
};
